/**
 * Owner 구독 정지/만료 안내 페이지
 * Suspended/Expired Subscription Notice Page
 *
 * 구독 상태가 suspended 또는 expired일 때 표시되는 페이지
 * - suspended: 체험 기간 만료 후 미결제
 * - expired: 유료 플랜 만료
 */

import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  XCircleIcon,
  UserPlusIcon,
  TableCellsIcon,
  CreditCardIcon,
  MagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

type SubscriptionStatus = 'suspended' | 'expired';

interface StatusConfig {
  title: string;
  description: string;
  icon: React.ElementType;
  iconBgColor: string;
  iconColor: string;
  buttonText: string;
}

const statusConfigs: Record<SubscriptionStatus, StatusConfig> = {
  suspended: {
    title: '체험 기간이 종료되었습니다',
    description: '서비스를 계속 이용하시려면 플랜을 선택해주세요',
    icon: ExclamationTriangleIcon,
    iconBgColor: 'bg-yellow-100',
    iconColor: 'text-yellow-600',
    buttonText: '플랜 선택하기',
  },
  expired: {
    title: '구독이 만료되었습니다',
    description: '서비스를 계속 이용하시려면 플랜을 갱신해주세요',
    icon: XCircleIcon,
    iconBgColor: 'bg-red-100',
    iconColor: 'text-red-600',
    buttonText: '플랜 갱신하기',
  },
};

interface Restriction {
  icon: React.ElementType;
  text: string;
  available: boolean;
}

const restrictions: Restriction[] = [
  { icon: UserPlusIcon, text: '새 회원 등록', available: false },
  { icon: TableCellsIcon, text: '좌석 배치 수정', available: false },
  { icon: CreditCardIcon, text: '결제 처리', available: false },
  { icon: MagnifyingGlassIcon, text: '조회 기능', available: true },
];

const SuspendedPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // URL 파라미터 또는 기본값으로 상태 결정
  const statusParam = searchParams.get('status') as SubscriptionStatus | null;
  const status: SubscriptionStatus = statusParam === 'expired' ? 'expired' : 'suspended';

  const config = statusConfigs[status];
  const IconComponent = config.icon;

  // 스테이징 환경 감지
  const getSubscriptionPath = () => {
    const hostname = window.location.hostname;
    if (hostname === '192.168.0.38' || hostname === '180.70.238.90') {
      return '/studyroom/owner/subscription/plans';
    }
    return '/owner/subscription/plans';
  };

  const getContactPath = () => {
    const hostname = window.location.hostname;
    if (hostname === '192.168.0.38' || hostname === '180.70.238.90') {
      return '/studyroom/contact';
    }
    return '/contact';
  };

  const handleSelectPlan = () => {
    navigate(getSubscriptionPath());
  };

  const handleContact = () => {
    navigate(getContactPath());
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* 메인 카드 */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {/* 아이콘 */}
          <div className="flex justify-center mb-6">
            <div className={`w-20 h-20 ${config.iconBgColor} rounded-full flex items-center justify-center`}>
              <IconComponent className={`w-10 h-10 ${config.iconColor}`} />
            </div>
          </div>

          {/* 상태 메시지 */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {config.title}
            </h1>
            <p className="text-gray-600">
              {config.description}
            </p>
          </div>

          {/* 제한 사항 안내 */}
          <div className="bg-gray-50 rounded-xl p-5 mb-8">
            <h2 className="text-sm font-semibold text-gray-700 mb-4">
              현재 이용 가능 상태
            </h2>
            <div className="space-y-3">
              {restrictions.map((restriction, index) => {
                const RestrictionIcon = restriction.icon;
                return (
                  <div
                    key={index}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center space-x-3">
                      <RestrictionIcon className="w-5 h-5 text-gray-400" />
                      <span className="text-sm text-gray-700">{restriction.text}</span>
                    </div>
                    {restriction.available ? (
                      <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                        사용 가능
                      </span>
                    ) : (
                      <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded-full">
                        사용 불가
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* 버튼들 */}
          <div className="space-y-3">
            <button
              onClick={handleSelectPlan}
              className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              <CreditCardIcon className="w-5 h-5" />
              <span>{config.buttonText}</span>
            </button>

            <button
              onClick={handleContact}
              className="w-full py-3 px-4 bg-white hover:bg-gray-50 text-gray-700 font-medium border border-gray-300 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              <ChatBubbleLeftRightIcon className="w-5 h-5" />
              <span>문의하기</span>
            </button>
          </div>
        </div>

        {/* 추가 정보 */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            결제 관련 문의: support@newattend.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default SuspendedPage;
